import { Grid } from '@mui/material'
import { CardHeader } from './CardHeader'

import Logger from 'services/Logger'
// import { CompareToBuilder } from 'apache-commons-lang' // Assuming you have the CompareToBuilder available

import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import TFMEventsList from './TFMEventsList'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  MasterLabelLabel,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { t } from 'i18next'
import dayjs, { Dayjs } from 'dayjs'
import { eventComparator, isLate } from 'utils/api/events'
import { updateEventOnServer } from 'services/api/event'
import Database from 'services/db/Database'

import './styles.scss'
import { localEventByLocalId } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { addTempTaskToDate } from './TFMEventsView'
import { useAppUIType } from 'components/providers/AppUITypeProvider'
import { useState } from 'react'
import { isToday } from 'date-fns'
import OffsetFromBottomDivider from 'components/dividers/OffsetFromBottomDivider'
import { getDefaultTimezone } from 'index'
import { useEventPopover } from 'components/calendar/eventPopover/EventPopoverProvider'

export enum DroppableColumns {
  today = 'today',
  tomorrow = 'tomorrow',
  later = 'later'
}

interface TFMEventsColumnsProps {
  type: DocType
  events?: Model<EventDoc>[] | EventDoc[]
  userSettings?: Model<UserSettingsDoc>
  account?: Model<AccountDoc>
}

/**
 * Gets the event's start date as it appears in the user's local timezone.
 * This ensures all date comparisons happen in the same timezone reference frame.
 */
function getEventDateInUserTimezone(
  event: EventDoc,
  userTimezone: string
): Dayjs {
  const timestamp = Number(event.StartDate) * 1000

  // For all-day events, the date component is the actual event date
  // and doesn't need timezone conversion
  if (event.AllDay === '1') {
    return dayjs(timestamp)
  }

  // For timed events, convert to user's timezone to see what date/time
  // the event represents from the user's perspective
  return dayjs(timestamp).tz(userTimezone)
}

function getEventDateStringInUserTimezone(
  event: EventDoc,
  userTimezone: string
): string {
  return getEventDateInUserTimezone(event, userTimezone)
    .startOf('day')
    .format('YYYY-MM-DD')
}

export default function TFMEventsColumns(props: TFMEventsColumnsProps) {
  const userTimezone = getDefaultTimezone()
  const today = dayjs().tz(userTimezone).startOf('day')

  const tomorrow = today.add(1, 'day')
  const later = today.add(2, 'day')
  const formatForDates = 'dddd, MMM DD'
  const appUiTypeContext = useAppUIType()

  const [isDragging, setIsDragging] = useState(false)

  let todayBucket = props.events?.filter((event) => {
    const eventDateStr = getEventDateStringInUserTimezone(event, userTimezone)
    const todayDateStr = today.format('YYYY-MM-DD')

    return eventDateStr === todayDateStr || isLate(event, true)
  })

  for (const event of props.events
    ?.filter(
      (event) => event.Recurrence && typeof event.Recurrence !== 'string'
    )
    .slice()
    .reverse() ?? []) {
    if (
      (isToday(Number(event.StartDate) * 1000) || isLate(event)) &&
      !todayBucket?.find((todayEvent) => event._id === todayEvent._id)
    ) {
      todayBucket?.push(event)
    }
  }

  todayBucket = todayBucket
    ?.sort(eventComparator)
    .map((event) => {
      // Create a new object with the same properties as the original event
      const updatedEvent: EventDoc = {
        ...Database.toServerObject(event),
        lateOriginalStartTime: event.StartDate,
        lateOriginalEndTime: event.EndDate,
        id: event.id,
        SupplementaryGroupsIDs: event.SupplementaryGroupsIDs
      }
      // Update the 'late' field to a new value
      updatedEvent.late = isLate(updatedEvent)

      // Return the updated event object
      return updatedEvent
    })
    .filter((event) => event.Status !== EventStatus.REMOVED)
  const tomorrowBucket = props.events
    ?.filter(
      (event) =>
        tomorrow.format('YYYY-MM-DD') ===
        getEventDateStringInUserTimezone(event, userTimezone)
    )
    .sort(eventComparator)

  const laterBucket = props.events
    ?.filter((event) => {
      const eventDateStr = getEventDateStringInUserTimezone(event, userTimezone)
      const eventDateObj = getEventDateInUserTimezone(
        event,
        userTimezone
      ).startOf('day')

      return (
        later.format('YYYY-MM-DD') === eventDateStr ||
        eventDateObj.isAfter(later.endOf('day'))
      )
    })
    .filter(
      (item, index, self) => index === self.findIndex((i) => i._id === item._id)
    )
    .sort(eventComparator)

  const onDragEnd = (result: DropResult) => {
    // Logic to handle the item reorder
    // You can update your data structure here based on the result of the drag and drop
    LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
    setTimeout(() => {
      LocalStorage.remove(LocalStorageKey.DRAGGING)
    }, 25)

    Logger.blue('dnd', `result of drop `)
    Logger.blue('dnd', result)

    let event = props.events?.find(
      (event) => `${event._rev}${event.StartDate}` === result.draggableId
    )

    //for pending events we are using inner db 'id' field
    if (!event) {
      event = props.events?.find((event) => event.id === result.draggableId)
    }

    Logger.blue('dnd', event)

    setShowEvent(event?.id)

    const newPosition = result.destination?.index
    Logger.blue('dnd:position', newPosition)
    if (newPosition != undefined && newPosition >= 0) {
      //define events which might be above and below our dropped event
      let prevEventPosition = newPosition - 1
      let nextEventPosition = newPosition

      let prevEvent: EventDoc | undefined
      let nextEvent: EventDoc | undefined

      Logger.blue('dnd:prevposition', prevEventPosition)
      Logger.blue('dnd:nextposition', nextEventPosition)

      let bucket: EventDoc[] | undefined = todayBucket

      switch (result.destination?.droppableId as DroppableColumns) {
        case DroppableColumns.today:
          bucket = todayBucket
          break

        case DroppableColumns.later:
          bucket = laterBucket
          break

        case DroppableColumns.tomorrow:
          bucket = tomorrowBucket

          break

        default:
          break
      }

      prevEvent = bucket?.at(prevEventPosition)
      nextEvent = bucket?.at(nextEventPosition)

      Logger.blue('dnd:bucket', bucket)
      Logger.blue('dnd:prev', prevEvent)
      Logger.blue('dnd:next', nextEvent)

      bucket = bucket?.filter(
        (e) =>
          e.Status === EventStatus.ACTIVE || e.Status === EventStatus.COMPLETED
      )

      let newStartDate: Dayjs | undefined = undefined

      if (result.destination) {
        let rank = event?.Rank
        const duration = event
          ? Number(event.EndDate) - Number(event.StartDate)
          : 1000 * 60
        const eventStart = event
          ? dayjs(Number(event.StartDate) * 1000)
          : dayjs()
        let allDay = event ? event.AllDay : '0'

        if (prevEvent || nextEvent) {
          if (newPosition === 0 && nextEvent) {
            rank = (Number(nextEvent?.Rank) - 100).toString()
            allDay = nextEvent.AllDay
            newStartDate = dayjs.unix(Number(nextEvent.StartDate))
          } else if (newPosition === (bucket?.length ?? 0) - 1) {
            //we dropped to the end of the list
            rank = (Number(nextEvent?.Rank) + 100).toString()
            allDay = nextEvent?.AllDay
            newStartDate = dayjs.unix(Number(nextEvent?.StartDate))
          } else if (prevEvent && nextEvent) {
            //we dropped inbetween
            if (prevEvent.StartDate === nextEvent.StartDate) {
              //we dropped inbetween
              rank = (
                (Number(nextEvent.Rank) + Number(prevEvent.Rank)) /
                2
              ).toString()
            } else {
              rank = (Number(prevEvent.Rank) + 100).toString()
            }

            newStartDate = dayjs.unix(Number(prevEvent?.StartDate))
            allDay = prevEvent.AllDay
          } else if (prevEvent && !nextEvent) {
            rank = (Number(prevEvent.Rank) + 100).toString()
            allDay = prevEvent.AllDay
            newStartDate = dayjs.unix(Number(prevEvent?.StartDate))
          }
        }

        if (!newStartDate) {
          switch (result.destination?.droppableId as DroppableColumns) {
            case DroppableColumns.today:
              newStartDate = dayjs(today)
                .hour(eventStart.hour())
                .minute(eventStart.minute())
              break
            case DroppableColumns.tomorrow:
              {
                newStartDate = dayjs(tomorrow)
                  .hour(eventStart.hour())
                  .minute(eventStart.minute())
              }
              break
            case DroppableColumns.later:
              newStartDate = dayjs(later)
                .hour(eventStart.hour())
                .minute(eventStart.minute())
              break
          }
        }

        if (!event || event.someday === '1') {
          LocalStorage.set(
            LocalStorageKey.SOMEDAY_DATE,
            newStartDate.unix().toString()
          )
          LocalStorage.set(LocalStorageKey.SOMEDAY_ALL_DAY, allDay)
          return
        }

        if (event !== undefined) {
          Database.write(async () => {
            if (event !== undefined)
              (await localEventByLocalId(event.id ?? '')).update((local) => {
                local.StartDate = newStartDate?.unix().toString()
                local.EndDate = newStartDate
                  ?.add(duration, 'second')
                  .unix()
                  .toString()
                local.Rank = rank
              })
          }).then(() => {
            if (event && event._id && event._rev) {
              updateEventOnServer(event, {
                StartDate: newStartDate?.unix().toString(),
                EndDate: newStartDate
                  ?.add(duration, 'second')
                  .unix()
                  .toString(),
                Rank: rank,
                SupplementaryGroupsIDs: []
              })
            }
          })
        }
      }
    }

    setIsDragging(false)
  }

  const [showEvent, setShowEvent] = useState<string | undefined>(undefined)
  const { closePopover } = useEventPopover()
  return (
    <DragDropContext
      onDragStart={() => {
        setShowEvent(undefined)
        closePopover()
        LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
        LocalStorage.set(LocalStorageKey.DRAGGING, '1')
        setIsDragging(true)
      }}
      onDragEnd={onDragEnd}
    >
      <Grid
        container
        flexWrap={'nowrap'}
        style={{
          height: '100%'
        }}
      >
        {buildColumn({
          title: t('today'),
          subTitle: today.format(formatForDates),
          events: todayBucket,
          droppableId: DroppableColumns.today,
          type: props.type,
          userSettings: props.userSettings,
          account: props.account,
          label: appUiTypeContext.selectedLabel,
          showEvent: showEvent,
          setShowEvent: setShowEvent,
          isDragging: isDragging
        })}
        {buildColumn({
          title: t('tomorrow'),
          events: tomorrowBucket?.filter(
            (event) =>
              event.Status === EventStatus.ACTIVE ||
              event.Status === EventStatus.COMPLETED
          ),
          subTitle: tomorrow.format(formatForDates),
          droppableId: DroppableColumns.tomorrow,
          showDividers: true,
          type: props.type,
          userSettings: props.userSettings,
          account: props.account,
          label: appUiTypeContext.selectedLabel,
          showEvent: showEvent,
          setShowEvent: setShowEvent,
          isDragging: isDragging
        })}
        {buildColumn({
          title: t('later'),
          events: laterBucket?.filter(
            (event) =>
              event.Status === EventStatus.ACTIVE ||
              event.Status === EventStatus.COMPLETED
          ),
          subTitle: later.format(formatForDates),
          droppableId: DroppableColumns.later,
          type: props.type,
          userSettings: props.userSettings,
          account: props.account,
          label: appUiTypeContext.selectedLabel,
          showEvent: showEvent,
          setShowEvent: setShowEvent,
          isDragging: isDragging
        })}
      </Grid>
    </DragDropContext>
  )
}

interface ColumnProps {
  title: string
  subTitle: string
  droppableId: string
  type: DocType
  events?: EventDoc[]
  showDividers?: boolean
  userSettings?: Model<UserSettingsDoc>
  account?: Model<AccountDoc>
  label?: MasterLabelLabel
  showEvent?: string
  setShowEvent: (s?: string) => void
  isDragging?: boolean
}

function buildColumn(props: ColumnProps) {
  const columnClick = () => {
    if (props.isDragging) return
    if (!LocalStorage.get(LocalStorageKey.VISIBLE_EVENT)) {
      let date = dayjs()
        .tz(getDefaultTimezone())
        .set('hour', 12)
        .set('minute', 0)
        .set('second', 0)
        .set('millisecond', 0)

      switch (props.droppableId) {
        case DroppableColumns.today:
          date = date
            .add(1, 'hour')
            .set('minute', 0)
            .set('second', 0)
            .set('millisecond', 0)
          break
        case DroppableColumns.tomorrow:
          date = date.add(1, 'day')
          break
        case DroppableColumns.later:
          date = date.add(2, 'day')
          break
      }

      addTempTaskToDate(
        date.unix(),
        props.userSettings,
        props.type,
        props.label
      )
    } else {
      LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
      props.setShowEvent(undefined)
    }
  }

  return (
    <Grid item xs={4}>
      <Droppable droppableId={props.droppableId}>
        {(provided) => (
          <Grid
            style={{
              position: 'relative',
              height: '100%',
              flex: 1
            }}
          >
            <CardHeader title={props.title} subtitle={props.subTitle} />
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                height: '100%',
                width: '100%'
              }}
              onClick={columnClick}
            >
              <div
                className="scrollable-content"
                style={{
                  position: 'absolute',
                  top: 90,
                  bottom: 90,
                  left: 4,
                  overflow: 'auto',
                  right: 4
                }}
              >
                <TFMEventsList
                  userSettings={props.userSettings}
                  account={props.account}
                  droppableId={props.droppableId}
                  events={props.events}
                  showEvent={props.showEvent}
                  setShowEvent={props.setShowEvent}
                />
              </div>

              {provided.placeholder}
            </div>
            <OffsetFromBottomDivider />
          </Grid>
        )}
      </Droppable>
    </Grid>
  )
}
